<template>
  <Content
    class="col-span-full"
    :title="$route.name"
    :router="{
      name: 'RULES_LIST',
    }"
  >
    <el-form
      status-icon
      :model="ruleForm"
      :rules="rules"
      label-position="top"
      ref="ruleForm"
      label-width="120px"
      class="carpo-form demo-ruleForm"
      v-loading="loading"
    >
      <el-form-item
        :label="$t('TITLE')"
        prop="title"
        :rules="{
          required: true,
          message: $t('REQUIRED_FIELD', {
            type: $t('TITLE'),
          }),
          trigger: 'blur',
        }"
      >
        <el-input
          type="text"
          v-model="ruleForm.title"
          autocomplete="off"
          :disabled="isViewMode"
        ></el-input>
      </el-form-item>

      <el-form-item
        :label="$t('START_TIME')"
        prop="start_time"
        :rules="{
          required: true,
          message: $t('REQUIRED_FIELD', {
            type: $t('START_TIME'),
          }),
          trigger: 'blur',
        }"
      >
        <el-time-picker
          format="HH:mm"
          value-format="HH:mm"
          clearable
          v-model="ruleForm.start_time"
          :disabled="isViewMode"
        ></el-time-picker>
      </el-form-item>

      <el-form-item
        :label="$t('END_TIME')"
        prop="end_time"
        :rules="{
          required: true,
          message: $t('REQUIRED_FIELD', {
            type: $t('END_TIME'),
          }),
          trigger: 'blur',
        }"
      >
        <el-time-picker
          format="HH:mm"
          value-format="HH:mm"
          clearable
          v-model="ruleForm.end_time"
          :disabled="isViewMode"
        ></el-time-picker>
      </el-form-item>

      <el-form-item
        :label="$t('METRICS')"
        prop="metrics"
        v-loading="metricsLoading"
        :rules="{
          required: true,
          message: $t('REQUIRED_CHOOSE', {
            type: $t('METRICS'),
          }),
          trigger: 'blur',
        }"
      >
        <el-select
          v-model="ruleForm.metrics"
          :placeholder="$t('SELECT_PLACEHOLDER')"
          filterable
          clearable
          multiple
          collapse-tags
          @change="onMetricChange"
          :disabled="isViewMode"
        >
          <el-option
            v-for="item in metricsList"
            :key="item.id"
            :label="$i18n.locale == 'fa' ? item.fa_title : item.en_title"
            :value="item.id"
          >
            <div class="flex items-center">
              <span>
                {{ $i18n.locale == 'fa' ? item.fa_title : item.en_title }}
                <span class="text-xs text-gray-500 ml-1" v-if="item.unit">
                  ({{ item.unit }})
                </span>
              </span>
            </div>
          </el-option>
        </el-select>
      </el-form-item>

      <VehicleSelect
        prop="company_vehicles"
        :model="ruleForm.company_vehicles"
        @update="onVehicleChange"
        :multiple="true"
        :collapseTags="true"
        :disabled="
          isViewMode || !ruleForm.metrics || ruleForm.metrics.length === 0
        "
        :metricsFilter="ruleForm.metrics"
        :rules="{
          required: true,
          message: $t('REQUIRED_CHOOSE', {
            type: $t('VEHICLE_TITLE'),
          }),
          trigger: 'blur',
        }"
      />

      <!-- Rules Section -->
      <div v-if="showRulesSection" class="col-span-full">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg font-medium">
            {{ $t('CONDITION_RULES') }}
          </h3>
          <el-button
            v-if="!isViewMode"
            type="primary"
            size="small"
            @click="addRule"
          >
            {{ $t('ADD_RULE') }}
          </el-button>
        </div>

        <div
          v-for="(rule, index) in ruleForm.rule_conditions"
          :key="index"
          class="mb-4 p-3 pt-8 border border-gray-200 rounded-lg"
        >
          <div class="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
            <el-form-item
              :label="$t('METRICS')"
              :prop="`rule_conditions.${index}.metric`"
              :rules="{
                required: true,
                message: $t('REQUIRED_CHOOSE', {
                  type: $t('METRICS'),
                }),
                trigger: 'blur',
              }"
            >
              <el-select
                v-model="rule.metric"
                :placeholder="$t('SELECT_PLACEHOLDER')"
                filterable
                clearable
                :disabled="isViewMode"
                @change="onRuleMetricChange(index)"
              >
                <el-option
                  v-for="metric in selectedMetrics"
                  :key="metric.id"
                  :label="
                    $i18n.locale == 'fa' ? metric.fa_title : metric.en_title
                  "
                  :value="metric.id"
                >
                  <div class="flex items-center">
                    <span>
                      {{
                        $i18n.locale == 'fa' ? metric.fa_title : metric.en_title
                      }}
                      <span class="text-xs text-gray-500 ml-1">
                        ({{ metric.unit }})
                      </span>
                    </span>
                  </div>
                </el-option>
              </el-select>
            </el-form-item>

            <el-form-item
              :label="$t('OPERATOR')"
              :prop="`rule_conditions.${index}.operator`"
              :rules="{
                required: true,
                message: $t('REQUIRED_CHOOSE', {
                  type: $t('OPERATOR'),
                }),
                trigger: 'blur',
              }"
            >
              <el-select
                v-model="rule.operator"
                :placeholder="$t('SELECT_PLACEHOLDER')"
                filterable
                clearable
                :disabled="isViewMode || !rule.metric"
              >
                <el-option
                  v-for="operator in getRuleMetricOperators(rule.metric)"
                  :key="operator.id"
                  :label="
                    $i18n.locale == 'fa' ? operator.fa_title : operator.en_title
                  "
                  :value="operator.id"
                >
                  <div class="flex items-center">
                    <span class="mx-2 font-mono text-sm ltr">
                      {{ operator.symbol }}
                    </span>
                    <span>
                      {{
                        $i18n.locale == 'fa'
                          ? operator.fa_title
                          : operator.en_title
                      }}
                    </span>
                  </div>
                </el-option>
              </el-select>
            </el-form-item>

            <!-- Regular threshold value input for non-fence metrics -->
            <el-form-item
              v-if="!isFenceMetric(rule.metric)"
              :label="
                $t('THRESHOLD_VALUE') +
                (getRuleMetricUnit(rule.metric)
                  ? ' (' + getRuleMetricUnit(rule.metric) + ')'
                  : '')
              "
              :prop="`rule_conditions.${index}.threshold_value`"
              :rules="{
                required: true,
                message: $t('REQUIRED_FIELD', {
                  type: $t('THRESHOLD_VALUE'),
                }),
                trigger: 'blur',
              }"
            >
              <el-input
                @input="rule.threshold_value = toEnglish(rule.threshold_value)"
                v-model="rule.threshold_value"
                autocomplete="off"
                :disabled="isViewMode"
                class="ltr"
              ></el-input>
            </el-form-item>

            <!-- Fence selection for fence metrics -->
            <el-form-item
              v-if="isFenceMetric(rule.metric)"
              :label="$t('SELECT_FENCE_AREA')"
              :prop="`rule_conditions.${index}.fence_coordinates`"
              :rules="{
                required: true,
                message: $t('REQUIRED_FIELD', {
                  type: $t('SELECT_FENCE_AREA'),
                }),
                trigger: 'blur',
              }"
            >
              <div class="flex items-center gap-2 mt-2 mr-4">
                <el-button
                  v-if="!rule.fence_coordinates"
                  type="default"
                  size="small"
                  @click="openFenceDialog(index)"
                  :disabled="isViewMode"
                >
                  {{ $t('SELECT_FENCE_AREA') }}
                </el-button>
                <div v-else class="flex items-center mr-2 gap-2">
                  <el-button
                    type="text"
                    size="small"
                    @click="openFenceDialog(index)"
                  >
                    {{ isViewMode ? $t('VIEW_GEOFENCE') : $t('CHANGE_FENCE') }}
                  </el-button>
                  <el-button
                    v-if="!isViewMode"
                    type="text"
                    size="small"
                    @click="clearFence(index)"
                    class="text-red-500"
                  >
                    {{ $t('CLEAR') }}
                  </el-button>
                </div>
              </div>
            </el-form-item>

            <el-form-item
              v-if="!isViewMode && ruleForm.rule_conditions.length > 1"
            >
              <el-button
                type="danger"
                size="small"
                @click="removeRule(index)"
                icon="el-icon-delete"
              ></el-button>
            </el-form-item>
          </div>
        </div>
      </div>

      <el-form-item
        class="col-span-full"
        :label="$t('description')"
        prop="description"
      >
        <el-input
          type="textarea"
          v-model="ruleForm.description"
          :disabled="isViewMode"
        ></el-input>
      </el-form-item>

      <FormConfirmation
        v-if="!isViewMode"
        @resetForm="resetForm('ruleForm')"
        @submitForm="submitForm('ruleForm')"
        :loading="submitLoading"
      />
    </el-form>

    <!-- Fence Selection Dialog -->
    <customDialog
      v-if="showFenceDialog"
      :title="isViewMode ? $t('VIEW_GEOFENCE') : $t('SELECT_FENCE_AREA')"
      childClass="info_msg fence_dialog"
      :show="showFenceDialog"
      :showCancel="true"
      :showFooter="!isViewMode"
      customWidth="80%"
      :showClose="true"
      @show="showFenceDialog = $event"
      @close="closeFenceDialog()"
      @confirmButn="confirmFenceSelection()"
    >
      <Notice class="col-span-full mb-4 border-0" v-if="!isViewMode">
        {{ $t('AreaInstructions') }}
      </Notice>

      <div
        class="col-span-full mb-4"
        id="fenceMap"
        ref="fenceMapElement"
        style="height: 400px"
      ></div>
    </customDialog>
  </Content>
</template>

<script>
import { resetForm } from '@/mixins/resetForm'
import { jalaliToGregorian } from '@/mixins/jalaliToGregorian'
import FormConfirmation from '@/components/elements/formConfirmation.vue'
import VehicleSelect from '@/components/elements/vehiclesSelect.vue'
import customDialog from '@/components/elements/dialog'
import Notice from '@/components/elements/notice'
import { searchAddress } from '@/mixins/searchAddress'
import config from '@/configs/configs.js'
import { addRule, editCondition, getMetrics } from '@/api/conditions'
import { fenceShape } from '@/api/fence'

export default {
  mixins: [resetForm, jalaliToGregorian, searchAddress],
  props: {
    condition: {
      type: Object,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    FormConfirmation,
    VehicleSelect,
    customDialog,
    Notice,
  },
  data() {
    return {
      submitLoading: false,
      metricsLoading: false,
      metricsList: [],
      fenceShapes: null,
      fenceShapesLoading: false,
      selectedMetric: null,
      showFenceDialog: false,
      fenceMap: null,
      currentFenceRuleIndex: null,
      currentFenceLayer: null,
      tempFenceCoordinates: null, // Temporary storage for fence being edited
      default: [35.71343059526645, 51.407055307645365],
      ruleForm: {
        title: '',
        start_time: '00:00',
        end_time: '23:59',
        description: '',
        company_vehicles: [],
        metrics: [],
        rule_conditions: [],
      },
      rules: {
        title: [
          {
            required: true,
            message: this.$i18n.t('REQUIRED_FIELD', {
              type: this.$i18n.t('TITLE'),
            }),
            trigger: 'blur',
          },
        ],
        start_time: [
          {
            required: true,
            message: this.$i18n.t('REQUIRED_FIELD', {
              type: this.$i18n.t('START_TIME'),
            }),
            trigger: 'blur',
          },
        ],
        end_time: [
          {
            required: true,
            message: this.$i18n.t('REQUIRED_FIELD', {
              type: this.$i18n.t('END_TIME'),
            }),
            trigger: 'blur',
          },
        ],
        company_vehicles: [
          {
            required: true,
            message: this.$i18n.t('REQUIRED_CHOOSE', {
              type: this.$i18n.t('VEHICLE_TITLE'),
            }),
            trigger: 'blur',
          },
        ],
        metrics: [
          {
            required: true,
            message: this.$i18n.t('REQUIRED_CHOOSE', {
              type: this.$i18n.t('METRICS'),
            }),
            trigger: 'blur',
          },
        ],
      },
    }
  },
  computed: {
    isEdit() {
      return this.condition && this.condition.id
    },
    isViewMode() {
      return this.$route.name.includes('VIEW')
    },
    selectedMetrics() {
      if (!this.ruleForm.metrics || this.ruleForm.metrics.length === 0) {
        return []
      }
      return this.metricsList.filter((metric) =>
        this.ruleForm.metrics.includes(metric.id)
      )
    },
    showRulesSection() {
      return (
        this.ruleForm.metrics &&
        this.ruleForm.metrics.length > 0 &&
        this.ruleForm.company_vehicles &&
        this.ruleForm.company_vehicles.length > 0
      )
    },
  },
  watch: {
    condition: {
      handler(newCondition) {
        if (newCondition && !this.loading) {
          this.populateForm(newCondition)
        }
      },
      immediate: true,
    },
  },
  created() {
    this.fetchMetrics()
    this.fetchFenceShapes()
  },
  methods: {
    fetchMetrics() {
      this.metricsLoading = true
      getMetrics.api().then((res) => {
        if (res) {
          this.metricsList = res

          if (this.condition && !this.loading) {
            this.populateForm(this.condition)
          }
        }
        this.metricsLoading = false
      })
    },

    fetchFenceShapes() {
      this.fenceShapesLoading = true
      fenceShape.api().then((res) => {
        if (res) {
          this.fenceShapes = res
        }
        this.fenceShapesLoading = false
      })
    },

    populateForm(conditionData) {
      const vehicleIds = conditionData.company_vehicles.map(
        (vehicle) => vehicle.id
      )

      const metricIds =
        conditionData.rule_conditions.length > 0
          ? [
              ...new Set(
                conditionData.rule_conditions.map((rule) => rule.metric.id)
              ),
            ]
          : []

      this.ruleForm = {
        title: conditionData.title || '',
        start_time: conditionData.start_time || '',
        end_time: conditionData.end_time || '',
        description: conditionData.description || '',
        company_vehicles: vehicleIds,
        metrics: metricIds,
        rule_conditions: conditionData.rule_conditions.map((rule) => {
          const isFence = this.isFenceMetric(rule.metric.id)
          console.log('Processing rule:', rule, 'isFence:', isFence)

          let fenceCoordinates = null
          if (isFence && rule.threshold?.value) {
            // Deep clone to avoid Vue reactivity issues
            fenceCoordinates = JSON.parse(JSON.stringify(rule.threshold.value))
            console.log('Fence coordinates extracted:', fenceCoordinates)

            // Convert shape name to shape ID for internal use
            if (fenceCoordinates && typeof fenceCoordinates.type === 'string') {
              const shapeId = this.shapeDic(fenceCoordinates.type)
              if (shapeId) {
                fenceCoordinates.type = shapeId
                console.log(
                  'Converted shape name to ID:',
                  fenceCoordinates.type
                )
              }
            }

            // Handle coordinate format - ensure coordinates are in the right format
            if (fenceCoordinates.coordinates) {
              // If coordinates are nested arrays (like [[[lng, lat], ...]]), flatten them
              if (
                Array.isArray(fenceCoordinates.coordinates) &&
                Array.isArray(fenceCoordinates.coordinates[0]) &&
                Array.isArray(fenceCoordinates.coordinates[0][0])
              ) {
                // This is the nested format [[[lng, lat], ...]], flatten to [[lng, lat], ...]
                fenceCoordinates.coordinates = fenceCoordinates.coordinates[0]
                console.log(
                  'Flattened nested coordinates:',
                  fenceCoordinates.coordinates
                )
              }
            }

            // Handle old circle format conversion if needed
            // Old format: { type, coordinates: { radius, coordinates } }
            // New format: { type, coordinates: [lng, lat], radius }
            const shapeTypeName =
              typeof fenceCoordinates.type === 'number'
                ? this.getShapeNameById(fenceCoordinates.type)
                : fenceCoordinates.type

            if (
              shapeTypeName === 'circle' &&
              fenceCoordinates.coordinates &&
              typeof fenceCoordinates.coordinates === 'object' &&
              fenceCoordinates.coordinates.radius
            ) {
              // Convert old format to new format
              console.log('Converting old circle format to new format')
              fenceCoordinates = {
                type: fenceCoordinates.type,
                coordinates: fenceCoordinates.coordinates.coordinates,
                radius: fenceCoordinates.coordinates.radius,
              }
            }
          }

          return {
            id: rule.id,
            threshold_value: isFence ? null : rule.threshold.value,
            fence_coordinates: fenceCoordinates,
            metric: rule.metric.id,
            operator: rule.operator.id,
          }
        }),
      }
    },

    onMetricChange() {
      if (!this.isEdit) {
        this.ruleForm.company_vehicles = []
        this.ruleForm.rule_conditions = []
      }
    },

    onVehicleChange(vehicles) {
      this.ruleForm.company_vehicles = vehicles

      if (
        !this.isEdit &&
        vehicles &&
        vehicles.length > 0 &&
        this.ruleForm.rule_conditions.length === 0 &&
        this.ruleForm.metrics.length > 0
      ) {
        this.addRule()
      }

      if (!vehicles || vehicles.length === 0) {
        this.ruleForm.rule_conditions = []
      }
    },

    addRule() {
      this.ruleForm.rule_conditions.push({
        threshold_value: null,
        fence_coordinates: null,
        metric:
          this.ruleForm.metrics.length > 0 ? this.ruleForm.metrics[0] : null,
        operator: null,
      })
    },

    removeRule(index) {
      if (this.ruleForm.rule_conditions.length > 1) {
        this.ruleForm.rule_conditions.splice(index, 1)
      }
    },

    onRuleMetricChange(index) {
      // Clear operator, threshold value, and fence coordinates when metric changes
      this.ruleForm.rule_conditions[index].operator = null
      this.ruleForm.rule_conditions[index].threshold_value = null
      this.ruleForm.rule_conditions[index].fence_coordinates = null
    },

    getRuleMetricOperators(metricId) {
      if (!metricId) return []
      const metric = this.metricsList.find((m) => m.id === metricId)
      return metric ? metric.operators : []
    },

    getRuleMetricUnit(metricId) {
      if (!metricId) return ''
      const metric = this.metricsList.find((m) => m.id === metricId)
      return metric ? metric.unit : ''
    },

    isFenceMetric(metricId) {
      if (!metricId) return false
      const metric = this.metricsList.find((m) => m.id === metricId)
      return metric ? metric.code === 'fence' : false
    },

    openFenceDialog(ruleIndex) {
      this.currentFenceRuleIndex = ruleIndex
      // Copy existing fence coordinates to temporary state
      if (this.ruleForm.rule_conditions[ruleIndex].fence_coordinates) {
        this.tempFenceCoordinates = JSON.parse(
          JSON.stringify(
            this.ruleForm.rule_conditions[ruleIndex].fence_coordinates
          )
        )
        console.log(this.tempFenceCoordinates, 'temp')
      } else {
        this.tempFenceCoordinates = null
      }
      this.showFenceDialog = true
      this.$nextTick(() => {
        this.loadFenceMap()
        // If there's an existing fence, display it after a short delay to ensure map is ready
        if (this.tempFenceCoordinates) {
          setTimeout(() => {
            this.displayExistingFence(this.tempFenceCoordinates)
          }, 500)
        }
      })
    },

    closeFenceDialog() {
      this.showFenceDialog = false
      this.currentFenceRuleIndex = null
      this.tempFenceCoordinates = null
      if (this.fenceMap) {
        this.fenceMap.remove()
        this.fenceMap = null
      }
      this.currentFenceLayer = null
    },

    confirmFenceSelection() {
      if (this.currentFenceLayer) {
        try {
          const geoJson = this.currentFenceLayer.toGeoJSON()
          let coordinates
          let shapeType

          console.log('GeoJSON from layer:', geoJson)
          console.log('Layer type:', geoJson.geometry.type)

          // Handle different geometry types
          // Check if it's a circle (Leaflet circles are represented as Points in GeoJSON)
          if (
            geoJson.geometry.type === 'Point' &&
            this.currentFenceLayer.getRadius
          ) {
            // This is a circle
            console.log('Detected circle')
            shapeType = 'circle'
            // For circles, store in the format expected by API:
            // { type: "circle", coordinates: [lng, lat], radius: number }
            coordinates = geoJson.geometry.coordinates // Just the center point [lng, lat]
          } else if (geoJson.geometry.type === 'Polygon') {
            // Check if it's a rectangle or polygon
            // Rectangles typically have 5 points (4 corners + closing point) and right angles
            const coords = geoJson.geometry.coordinates[0]
            if (coords.length === 5 && this.isRectangle(coords)) {
              console.log('Detected rectangle')
              shapeType = 'polygon'
            } else {
              console.log('Detected polygon')
              shapeType = 'polygon'
            }
            coordinates = geoJson.geometry.coordinates[0]
          } else if (this.currentFenceLayer.getLatLngs) {
            // Fallback: try to get coordinates from the layer directly
            console.log('Using getLatLngs fallback')
            shapeType = 'polygon'
            const latLngs = this.currentFenceLayer.getLatLngs()
            if (Array.isArray(latLngs) && latLngs.length > 0) {
              // Handle nested arrays (polygon with holes)
              if (Array.isArray(latLngs[0])) {
                coordinates = latLngs[0].map((latLng) => [
                  latLng.lng,
                  latLng.lat,
                ])
              } else {
                coordinates = latLngs.map((latLng) => [latLng.lng, latLng.lat])
              }
            }
          }

          if (
            !coordinates ||
            (Array.isArray(coordinates) && coordinates.length === 0)
          ) {
            console.error('Failed to extract coordinates from layer')
            this.$notify.error({
              title: this.$t('ERROR'),
              message: this.$t('FAILED_TO_EXTRACT_FENCE_COORDINATES'),
              duration: 5000,
              customClass: 'error_msg',
            })
            return
          }

          console.log('Extracted coordinates:', coordinates)
          console.log('Shape type:', shapeType)

          // Get the shape ID from the API using shapeDic
          const shapeId = this.shapeDic(shapeType)

          if (!shapeId) {
            console.error('Failed to find shape ID for:', shapeType)
            this.$notify.error({
              title: this.$t('ERROR'),
              message: this.$t('FAILED_TO_FIND_SHAPE_TYPE'),
              duration: 5000,
              customClass: 'error_msg',
            })
            return
          }

          // Build fence data based on shape type
          let fenceData
          if (shapeType === 'circle') {
            // For circles: { type, coordinates: [lng, lat], radius }
            fenceData = {
              type: shapeId,
              coordinates: coordinates, // [lng, lat]
              radius: this.currentFenceLayer.getRadius(),
            }
          } else {
            // For polygons: { type, coordinates: [[lng, lat], ...] }
            fenceData = {
              type: shapeId,
              coordinates: [coordinates], // Wrap in array
            }
          }

          // Save to temporary state
          this.tempFenceCoordinates = JSON.parse(JSON.stringify(fenceData))

          // Save to form
          this.ruleForm.rule_conditions[
            this.currentFenceRuleIndex
          ].fence_coordinates = fenceData

          console.log('Fence data saved:', fenceData)
          console.log('Shape ID:', shapeId)

          this.closeFenceDialog()
        } catch (error) {
          console.error('Error confirming fence selection:', error)
          this.$notify.error({
            title: this.$t('ERROR'),
            message: this.$t('ERROR_SAVING_FENCE'),
            duration: 5000,
            customClass: 'error_msg',
          })
        }
      } else {
        this.$notify.warning({
          title: this.$t('WARNING'),
          message: this.$t('NO_FENCE_SELECTED'),
          duration: 5000,
          customClass: 'warning_msg',
        })
      }
    },

    isRectangle(coords) {
      // Simple check: a rectangle has 5 points (4 corners + closing point)
      // and opposite sides are parallel
      if (coords.length !== 5) return false

      // Check if first and last points are the same (closing the shape)
      const first = coords[0]
      const last = coords[4]
      if (first[0] !== last[0] || first[1] !== last[1]) return false

      // For a more accurate check, you could verify right angles
      // but this simple check should work for most cases
      return true
    },

    shapeDic(myShape) {
      if (!this.fenceShapes) return null
      const shape = this.fenceShapes.find(
        (shape) => shape.en_title.toLowerCase() === myShape.toLowerCase()
      )
      return shape ? shape.id : 'polygon'
    },

    getShapeNameById(shapeId) {
      if (!this.fenceShapes) return null
      const shape = this.fenceShapes.find((shape) => shape.id === shapeId)
      return shape ? shape.en_title.toLowerCase() : null
    },

    clearFence(ruleIndex) {
      this.ruleForm.rule_conditions[ruleIndex].fence_coordinates = null
    },

    displayExistingFence(fenceCoordinates) {
      if (!this.fenceMap || !fenceCoordinates) return

      console.log('Displaying existing fence:', fenceCoordinates)

      // Clear any existing layers first
      this.fenceMap.eachLayer((layer) => {
        if (layer._path) {
          this.fenceMap.removeLayer(layer)
        }
      })

      try {
        // Deep clone to remove Vue reactivity and get raw data
        const rawData = JSON.parse(JSON.stringify(fenceCoordinates))
        console.log('Raw data after parse:', rawData)
        console.log('Fence type (ID):', rawData.type)

        let layer

        // Convert shape ID to shape name for legacy compatibility
        let shapeType = rawData.type
        if (typeof rawData.type === 'number') {
          // If type is a number (shape ID), convert it to shape name
          shapeType = this.getShapeNameById(rawData.type)
          console.log('Converted shape ID to name:', shapeType)
        }

        // Check the type and handle accordingly
        if (shapeType === 'circle') {
          console.log('Displaying circle')
          // This is a circle
          // New format: { type: "circle", coordinates: [lng, lat], radius: number }
          const center = [rawData.coordinates[1], rawData.coordinates[0]] // Convert [lng, lat] to [lat, lng] for Leaflet
          const radius = rawData.radius

          layer = L.circle(center, {
            radius: radius,
            color: '#ff4949',
            fillColor: '#ff4949',
            fillOpacity: 0.3,
          })
        } else if (shapeType === 'rectangle' || shapeType === 'polygon') {
          // This is a polygon or rectangle
          console.log(`Displaying ${shapeType}`)
          let coordinates

          // Handle the array structure - coordinates should be wrapped in an array
          if (
            rawData.coordinates &&
            Array.isArray(rawData.coordinates) &&
            rawData.coordinates.length > 0
          ) {
            // If it's already an array of coordinates, use the first element
            coordinates =
              Array.isArray(rawData.coordinates[0]) &&
              Array.isArray(rawData.coordinates[0][0])
                ? rawData.coordinates[0]
                : rawData.coordinates
          } else {
            console.error('Invalid fence coordinates structure:', rawData)
            this.$notify.warning({
              title: this.$t('WARNING'),
              message: this.$t('INVALID_FENCE_DATA'),
              duration: 5000,
              customClass: 'warning_msg',
            })
            return
          }

          // Ensure coordinates is a valid array of coordinate pairs
          if (!Array.isArray(coordinates) || coordinates.length === 0) {
            console.error('Invalid coordinates array:', coordinates)
            this.$notify.warning({
              title: this.$t('WARNING'),
              message: this.$t('INVALID_COORDINATES'),
              duration: 5000,
              customClass: 'warning_msg',
            })
            return
          }

          console.log('Coordinates before conversion:', coordinates)

          // Convert from GeoJSON format [lng, lat] to Leaflet format [lat, lng]
          const leafletCoordinates = coordinates.map((coord) => [
            coord[1],
            coord[0],
          ])

          console.log(
            'Coordinates after conversion to Leaflet format:',
            leafletCoordinates
          )

          // Create a polygon from the coordinates
          layer = L.polygon(leafletCoordinates, {
            color: '#ff4949',
            fillColor: '#ff4949',
            fillOpacity: 0.3,
          })
        } else {
          // Fallback for old data format without type
          console.log('Displaying fence with unknown type, assuming polygon')
          let coordinates

          if (
            rawData.coordinates &&
            typeof rawData.coordinates === 'object' &&
            rawData.coordinates.radius &&
            rawData.coordinates.coordinates
          ) {
            // Very old circle format: { coordinates: { radius, coordinates } }
            const center = [
              rawData.coordinates.coordinates[1],
              rawData.coordinates.coordinates[0],
            ]
            const radius = rawData.coordinates.radius
            layer = L.circle(center, {
              radius: radius,
              color: '#ff4949',
              fillColor: '#ff4949',
              fillOpacity: 0.3,
            })
          } else if (
            rawData.radius &&
            Array.isArray(rawData.coordinates) &&
            rawData.coordinates.length === 2
          ) {
            // Old circle format: { coordinates: [lng, lat], radius }
            const center = [rawData.coordinates[1], rawData.coordinates[0]]
            const radius = rawData.radius
            layer = L.circle(center, {
              radius: radius,
              color: '#ff4949',
              fillColor: '#ff4949',
              fillOpacity: 0.3,
            })
          } else {
            // Old polygon format
            coordinates =
              Array.isArray(rawData.coordinates[0]) &&
              Array.isArray(rawData.coordinates[0][0])
                ? rawData.coordinates[0]
                : rawData.coordinates

            // Convert from GeoJSON format [lng, lat] to Leaflet format [lat, lng]
            const leafletCoordinates = coordinates.map((coord) => [
              coord[1],
              coord[0],
            ])

            layer = L.polygon(leafletCoordinates, {
              color: '#ff4949',
              fillColor: '#ff4949',
              fillOpacity: 0.3,
            })
          }
        }

        // Add the layer to the map
        layer.addTo(this.fenceMap)
        this.currentFenceLayer = layer

        // Enable editing mode for the layer
        layer.pm.enable({
          allowSelfIntersection: false,
        })

        // Add event listeners for editing
        layer.on('pm:edit', (e) => {
          this.currentFenceLayer = e.layer
          console.log('Existing fence edited')
        })

        layer.on('pm:remove', (e) => {
          this.currentFenceLayer = null
          this.tempFenceCoordinates = null
          console.log('Existing fence removed')
        })

        // Fit the map to show the layer
        this.fenceMap.fitBounds(layer.getBounds())

        console.log('Successfully displayed existing fence')
      } catch (error) {
        console.error('Error displaying existing fence:', error)
        // this.$notify.error({
        //   title: this.$t('ERROR'),
        //   message: this.$t('ERROR_DISPLAYING_FENCE'),
        //   duration: 5000,
        //   customClass: 'error_msg',
        // })
      }
    },

    loadFenceMap() {
      if (!this.$refs.fenceMapElement) return

      const map = L.map(this.$refs.fenceMapElement, {
        tap: false,
      }).setView(this.default, 10)

      L.tileLayer(config.tile, {
        attribution: '@ Carpo',
        id: 'mapbox/streets-v11',
        tileSize: 512,
        zoomOffset: -1,
        accessToken: config.leaflet,
      }).addTo(map)

      this.fenceMap = map
      var fg = L.featureGroup()
      map.pm.setLang('fa')

      map.on('pm:create', (e) => {
        if (this.currentFenceLayer) {
          this.$notify.warning({
            title: this.$t('WARNING'),
            message: this.$t('ONLY_ONE_FENCE_ALLOWED'),
            duration: 5000,
            customClass: 'warning_msg',
          })
          map.removeLayer(e.layer)
          return
        }

        let myLayer = e.layer
        fg.addLayer(myLayer)
        this.currentFenceLayer = myLayer
        this.currentFenceLayer.setStyle({
          color: '#ff4949',
          fillColor: '#ff4949',
          fillOpacity: 0.3,
        })

        // Enable editing for the new layer
        myLayer.pm.enable({
          allowSelfIntersection: false,
        })

        // Add event listeners for editing
        myLayer.on('pm:edit', (e) => {
          this.currentFenceLayer = e.layer
          console.log('Fence edited')
        })

        myLayer.on('pm:remove', (e) => {
          this.currentFenceLayer = null
          this.tempFenceCoordinates = null
          console.log('Fence removed')
        })
      })

      map.pm.addControls({
        position: 'topleft',
        drawPolyline: false,
        drawCircleMarker: false,
        drawMarker: false,
        drawCircle: true,
        drawRectangle: true,
        drawPolygon: true,
        drawText: false,
        cutPolygon: false,
        rotateMode: false,
        dragMode: false,
        editMode: false,
      })

      if (this.isViewMode) {
        map.pm.removeControls()
      }
    },

    submitForm(formName) {
      this.$refs[formName].validate((valid) => {
        if (valid) {
          if (this.ruleForm.rule_conditions.length === 0) {
            this.$notify.error({
              title: this.$i18n.t('ADD_AT_LEAST_ONE_RULE'),
              duration: 5000,
              customClass: 'error_msg',
            })
            return false
          }

          const incompleteRules = this.ruleForm.rule_conditions.some((rule) => {
            const isFence = this.isFenceMetric(rule.metric)
            return (
              !rule.operator ||
              (isFence ? !rule.fence_coordinates : !rule.threshold_value)
            )
          })

          if (incompleteRules) {
            this.$notify.error({
              title: this.$i18n.t('COMPLETE_ALL_RULES'),
              duration: 5000,
              customClass: 'error_msg',
            })
            return false
          }

          const actionPayload = {
            rule_conditions: this.ruleForm.rule_conditions.map((rule) => {
              const isFence = this.isFenceMetric(rule.metric)
              const baseRule = {
                ...(rule.id && { id: rule.id }),
                metric: rule.metric,
                operator: rule.operator,
              }

              if (isFence) {
                // Convert shape ID to shape name for the API
                const fenceData = JSON.parse(
                  JSON.stringify(rule.fence_coordinates)
                )
                const shapeTitle = this.getShapeNameById(fenceData.type)

                // Format coordinates properly for the API
                let formattedCoordinates = fenceData.coordinates

                // If coordinates are nested arrays (like [[[lng, lat], ...]]), flatten them
                if (
                  Array.isArray(fenceData.coordinates) &&
                  Array.isArray(fenceData.coordinates[0]) &&
                  Array.isArray(fenceData.coordinates[0][0])
                ) {
                  // This is the nested format [[[lng, lat], ...]], flatten to [[lng, lat], ...]
                  formattedCoordinates = fenceData.coordinates[0]
                }

                return {
                  ...baseRule,
                  threshold: {
                    value: {
                      ...fenceData,
                      type: shapeTitle || fenceData.type, // Use shape name instead of ID
                      coordinates: formattedCoordinates,
                    },
                  },
                }
              } else {
                return {
                  ...baseRule,
                  threshold: {
                    value: parseFloat(rule.threshold_value),
                  },
                }
              }
            }),
            title: this.ruleForm.title,
            start_time: this.ruleForm.start_time,
            end_time: this.ruleForm.end_time,
            description: this.ruleForm.description,
            company_vehicles: Array.isArray(this.ruleForm.company_vehicles)
              ? this.ruleForm.company_vehicles
              : [this.ruleForm.company_vehicles],
          }

          this.submitLoading = true

          const apiCall = this.isEdit
            ? editCondition.api(this.condition.id, actionPayload)
            : addRule.api(actionPayload)

          apiCall.then((res) => {
            this.submitLoading = false
            if (res) {
              this.$notify.success({
                title:
                  this.$t(this.$route.name) +
                  ' ' +
                  this.$t('SUCCESSFULLY_DONE'),
                duration: 5000,
                customClass: 'succ_msg',
              })

              if (!this.isEdit) {
                this.resetForm(formName)
              }

              this.$router.push({ name: 'RULES_LIST' })
            }
          })
        } else {
          this.$notify.error({
            title: this.$i18n.t('FIXED_ERRORS', {
              type: this.$i18n.t(this.$route.name),
            }),
            duration: 5000,
            customClass: 'error_msg',
          })
          return false
        }
      })
    },
  },
}
</script>
